'use client';

import Image from 'next/image';
import { ScrollReveal } from './ScrollReveal';

const focusAreas = [
  'Leadership Development',
  'Communications Skill',
  'Organizational Strategy',
  'Community Engagement',
];

function FocusMarquee() {
  // Triplicate items for seamless infinite scroll across full width
  const items = [...focusAreas, ...focusAreas, ...focusAreas];

  return (
    <div className="w-screen relative left-1/2 -translate-x-1/2 overflow-hidden py-6 border-y border-border/30 bg-background/80">
      <div className="flex animate-marquee">
        {items.map((item, index) => (
          <div key={index} className="flex items-center shrink-0">
            <span className="font-serif text-xl sm:text-2xl lg:text-3xl tracking-tight text-foreground whitespace-nowrap px-5">
              {item}
            </span>
            <span className="text-accent px-5">✦</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Bio() {
  return (
    <section className="pt-12 md:pt-24 lg:pt-32 pb-12 lg:pb-16 bg-muted/30 relative" id="about">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 noise-texture" />

      {/* Decorative elements */}
      <div className="hidden lg:block absolute top-24 right-0 w-48 h-px bg-gradient-to-l from-transparent via-accent/30 to-transparent" />
      <div className="hidden lg:block absolute bottom-24 left-0 w-32 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      {/* Large decorative quote mark */}
      <div className="hidden lg:block absolute top-32 left-8 xl:left-16">
        <span className="text-[200px] leading-none font-serif text-accent/[0.07] select-none">&ldquo;</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left column - Text content */}
          <div className="lg:col-span-7 space-y-8">
            <ScrollReveal>
              <h2 className="inline-flex items-center gap-3 text-sm font-medium tracking-wider uppercase text-accent">
                <span className="w-8 h-px bg-accent" />
                About Andrew
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="space-y-6">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Born in Belfast, and now living in California, Andrew has been blessed with 3 passports, British, Irish and American, affording him the wide privilege of serving local churches and church plants in a variety of settings.
                </p>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Andrew has extensive leadership and communication experience spanning decades and continents.  He has been in active ministry for over 30 years and travels widely, strengthening leaders, consulting with strategic teams, and sharing the Christian message in a creative, humorous and challenging style.
                </p>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Together with his French wife Isabelle, they founded &ldquo;To the World&rdquo; &mdash; a non profit organization dedicated to supporting church planters, ministry leaders and world changers across Europe and beyond. To join our mission, feel free to explore{' '}
                  <a href="https://totheworld.co" target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2 hover:text-accent/80 transition-colors">totheworld.co</a>
                  {' '}&mdash; together, we can change the world, one leader and one nation at a time!
                </p>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  On the home front, they have 4 children and 1 daughter-in-law: Ben and his wife Kaleena, Dan, Abigail and Nathan. Andrew and Isabelle are also Cillian&apos;s proud grandparents - an endless source of fun and entertainment!
                </p>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Andrew is passionate about &lsquo;real football&rsquo;, mountains and French food.
                </p>
              </div>
            </ScrollReveal>

          </div>

          {/* Right column - Image */}
          <div className="lg:col-span-5 order-first lg:order-last">
            <ScrollReveal direction="right" delay={150}>
              <div className="relative max-w-sm md:max-w-md lg:max-w-none mx-auto lg:mx-0">
                {/* Decorative offset frame */}
                <div className="absolute -inset-3 border border-accent/15 -z-10 -translate-x-3 -translate-y-3" />

                {/* Main image */}
                <div className="relative aspect-[4/3] bg-muted overflow-hidden shadow-xl">
                  <Image
                    src="/andrew-isabelle-2.jpeg"
                    alt="Andrew and Isabelle McCourt"
                    fill
                    className="object-cover transition-transform duration-700 hover:scale-105"
                    sizes="(max-width: 640px) 384px, (max-width: 1024px) 448px, 500px"
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />
                </div>

                {/* Caption */}
                <div className="mt-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-accent/40" />
                  <p className="text-sm text-muted-foreground italic">
                    Andrew & Isabelle McCourt
                  </p>
                </div>

                {/* Decorative corner */}
                <div className="absolute -bottom-2 -right-2 w-16 h-16 border-r-2 border-b-2 border-accent/20" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Full-width scrolling marquee */}
      <div className="mt-12 lg:mt-16">
        <FocusMarquee />
      </div>
    </section>
  );
}
